// UW (Underwriting) Journey API Service
// All methods call real backend APIs.
// External integrations not yet available (KYC IDV, Open Banking, Document S3)
// still send data to backend for persistence — frontend just provides mock input.

import { httpGet, httpPost, httpPut, httpPatch } from '../http';
import { femaCapture } from '../../fema/capture';
import type { UploadDocumentResponse } from '../types';
import {
  uwApplicationEndpoints,
  uwAddressEndpoints,
  uwConsentEndpoints,
  uwDirectDebitEndpoints,
  uwKycEndpoints,
  uwOnfidoEndpoints,
  uwFieldSourceEndpoints,
  uwDecisionEndpoints,
  uwSummaryEndpoints,
  uwOBEndpoints,
  uwOnboardingEndpoints,
  uwSoftCheckEndpoints,
  uwWebhookLogEndpoints,
  uwInviteLogEndpoints,
  UW_DOCUMENTS_UPLOAD_HOT_STORAGE_PATH,
} from '../uwEndpoints';
import type {
  UWApplication,
  UWApplicationStatus,
  UWAddress,
  UWConsent,
  UWConsentType,
  UWDirectDebit,
  UWKycVerification,
  OnfidoInitResponse,
  OnfidoKycStatus,
  UWFieldSource,
  UWDecision,
  UWSummary,
} from '@/shared/types/uw';
import type { SoftCheckStatusResponse } from '@/shared/types/softCheck';

/**
 * Helper: unwrap `{ success, data }` envelope or return raw.
 * Throws a descriptive error when `success` is `false`.
 */
function unwrap<T>(response: any): T {
  const payload = response?.data;
  if (payload && typeof payload === 'object' && 'success' in payload) {
    if (payload.success === false) {
      const errMsg = payload.error?.message || payload.message || 'An unexpected error occurred';
      const errCode = payload.error?.code || 'UNKNOWN';
      const err = new Error(errMsg);
      (err as any).code = errCode;
      throw err;
    }
    if (payload.data !== undefined) {
      return payload.data as T;
    }
  }
  return payload as T;
}

const DEFAULT_UW_DOCUMENTS_API_ORIGIN = 'https://uw-apis-node.dynamatix.com';

function uwDocumentsApiBaseURL(): string {
  const envOrigin = import.meta.env.VITE_UW_DOCUMENTS_API_BASE_URL?.trim();
  if (envOrigin && envOrigin.length > 0) {
    return envOrigin;
  }
  return DEFAULT_UW_DOCUMENTS_API_ORIGIN;
}

function extractUwDocumentUploadBody(axiosResponse: { data: unknown }): unknown {
  const payload = axiosResponse.data;
  if (payload === null || typeof payload !== 'object') {
    return payload;
  }
  const p = payload as Record<string, unknown>;

  if (p.success === false) {
    const errObj = p.error;
    const errMsg =
      (typeof errObj === 'object' &&
        errObj !== null &&
        'message' in errObj &&
        String((errObj as { message?: unknown }).message)) ||
      String(p.message ?? 'Document upload failed');
    throw new Error(errMsg);
  }

  if (p.data !== undefined && p.data !== null && typeof p.data === 'object') {
    const hasSuccessKey = 'success' in p;
    if (!hasSuccessKey || p.success === true) {
      return p.data;
    }
  }

  return payload;
}

function stableIdFromBlobName(blobName: string): number {
  let h = 0;
  for (let i = 0; i < blobName.length; i += 1) {
    h = (Math.imul(31, h) + blobName.charCodeAt(i)) | 0;
  }
  const n = Math.abs(h);
  return n === 0 ? Date.now() % 2147483647 : n;
}

function coerceNumericCustomerId(value: string | number | null | undefined): number {
  if (value === null || value === undefined) {
    return 0;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const n = Number(String(value).trim());
  return Number.isFinite(n) ? n : 0;
}

function mapUwDocumentUploadToUploadDocumentResponse(
  body: unknown,
  ctx: {
    file: File;
    customerId: string | number | null | undefined;
    isDocUploadedFromDecl: string;
  },
): UploadDocumentResponse {
  if (!body || typeof body !== 'object') {
    throw new Error('Invalid document upload response');
  }
  const r = body as Record<string, unknown>;

  const blobNameRaw = r.blobName;
  const sizeBytesRaw = r.sizeBytes;
  if (typeof blobNameRaw === 'string' && typeof sizeBytesRaw === 'number') {
    const now = new Date().toISOString();
    const hotUrl = typeof r.url === 'string' ? r.url : '';
    const id = stableIdFromBlobName(blobNameRaw);
    const customerId = coerceNumericCustomerId(ctx.customerId);
    const fileName = ctx.file.name;
    return {
      creationDate: now,
      lastUpdateDate: now,
      createdBy: 0,
      lastUpdatedBy: 0,
      id,
      customerId,
      documentId: id,
      documentName: fileName,
      documentSize: String(sizeBytesRaw),
      fileName,
      shareId: typeof r.blobUrl === 'string' ? r.blobUrl : hotUrl,
      userId: 0,
      uploadDate: now,
      isDocUploadedFromDecl: ctx.isDocUploadedFromDecl,
      hotStorageUrl: hotUrl,
      blobName: blobNameRaw,
    };
  }

  const now = new Date().toISOString();
  const rawId = r.id ?? r.documentId ?? r.document_id;
  const id = typeof rawId === 'number' ? rawId : Number(rawId ?? 0);
  const customerId = coerceNumericCustomerId(
    r.customerId ?? r.customer_id ?? ctx.customerId,
  );
  const fileName = String(r.fileName ?? r.file_name ?? ctx.file.name);
  const documentName = String(r.documentName ?? r.document_name ?? fileName);
  const creationDate = String(
    r.creationDate ?? r.creation_date ?? r.uploadDate ?? r.upload_date ?? now,
  );
  const documentSize = String(r.documentSize ?? r.document_size ?? ctx.file.size);
  const documentId = Number(r.documentId ?? r.document_id ?? id ?? 0);

  return {
    creationDate,
    lastUpdateDate: String(r.lastUpdateDate ?? r.last_update_date ?? creationDate),
    createdBy: Number(r.createdBy ?? r.created_by ?? 0),
    lastUpdatedBy: Number(r.lastUpdatedBy ?? r.last_updated_by ?? 0),
    id: id || documentId,
    customerId,
    documentId: documentId || id,
    documentName,
    documentSize,
    fileName,
    shareId: String(r.shareId ?? r.share_id ?? ''),
    userId: Number(r.userId ?? r.user_id ?? 0),
    uploadDate: String(r.uploadDate ?? r.upload_date ?? creationDate),
    isDocUploadedFromDecl: String(
      r.isDocUploadedFromDecl ?? r.is_doc_uploaded_from_decl ?? ctx.isDocUploadedFromDecl,
    ),
  };
}

export class UWService {

  // ── Onboarding (batch save/retrieve) ────────────────

  static async saveOnboardingData(applicationId: string, data: Record<string, unknown>): Promise<{ saved: boolean }> {
    const res = await httpPut(uwOnboardingEndpoints.saveOnboarding(applicationId), data);
    return unwrap(res);
  }

  static async getOnboardingData(applicationId: string): Promise<Record<string, unknown> | null> {
    const res = await httpGet(uwOnboardingEndpoints.getOnboarding(applicationId));
    return unwrap<Record<string, unknown> | null>(res);
  }

  /**
   * Lock the onboarding form — user explicitly confirmed all details are correct
   * on the KYC Review screen. Idempotent server-side.
   */
  static async confirmOnboarding(applicationId: string): Promise<{
    onboardingConfirmed: boolean;
    onboardingConfirmedAt: string | null;
    status: string;
    alreadyConfirmed: boolean;
  }> {
    const res = await httpPost(uwOnboardingEndpoints.confirmOnboarding(applicationId));
    return unwrap(res);
  }

  // ── Application ──────────────────────────────────────

  static async getApplication(applicationId: string): Promise<UWApplication> {
    const res = await httpGet(uwApplicationEndpoints.getApplication(applicationId));
    return unwrap<UWApplication>(res);
  }

  static async getApplicationByCustomerId(customerId: string | number): Promise<UWApplication | null> {
    try {
      const res = await httpGet(uwApplicationEndpoints.getApplicationByCustomerId(customerId));
      return unwrap<UWApplication>(res);
    } catch {
      return null;
    }
  }

  static async getApplicationByEmail(email: string, customerId?: string | number): Promise<UWApplication | null> {
    try {
      const res = await httpGet(uwApplicationEndpoints.getApplicationByEmail(email, customerId));
      return unwrap<UWApplication>(res);
    } catch {
      return null;
    }
  }

  static async getApplicationStatus(applicationId: string): Promise<UWApplicationStatus> {
    const res = await httpGet(uwApplicationEndpoints.getApplicationStatus(applicationId));
    return unwrap<UWApplicationStatus>(res);
  }

  static async submitApplication(applicationId: string): Promise<{ submissionRef: string; submittedAt: string }> {
    const res = await httpPost(uwApplicationEndpoints.submitApplication(applicationId));
    return unwrap(res);
  }

  static async updateStage(applicationId: string, stage: string, lastRoute?: string): Promise<void> {
    await httpPut(`/iehub-customer/api/v1/uw/application/${applicationId}/stage`, { stage, lastRoute });
  }

  /** Terminal affordability decline from the I&E summary (Total leftover <= £0).
   *  Persists AFFORDABILITY_DECLINED so the journey stays ended on link reopen. */
  static async declineForAffordability(applicationId: string): Promise<void> {
    await httpPost(`/iehub-customer/api/v1/uw/application/${applicationId}/affordability-decline`);
  }

  // ── Address History ──────────────────────────────────

  static async getAddressHistory(customerId: string | number): Promise<UWAddress[]> {
    const res = await httpGet(uwAddressEndpoints.getAddressHistory(customerId));
    const data = unwrap<any>(res);
    return data?.addresses ?? data ?? [];
  }

  static async saveAddressHistory(customerId: string | number, addresses: UWAddress[]): Promise<{ saved: boolean }> {
    const res = await httpPost(uwAddressEndpoints.saveAddressHistory(customerId), { addresses });
    return unwrap(res);
  }

  // ── Consents ─────────────────────────────────────────

  static async getConsents(applicationId: string): Promise<Record<UWConsentType, UWConsent>> {
    const res = await httpGet(uwConsentEndpoints.getConsents(applicationId));
    const data = unwrap<any>(res);
    // Backend returns { consents: [...] } array, convert to record
    if (Array.isArray(data?.consents)) {
      const record: Record<string, UWConsent> = {};
      for (const c of data.consents) {
        record[c.type] = { accepted: c.accepted, timestamp: c.timestamp };
      }
      return record as Record<UWConsentType, UWConsent>;
    }
    return data ?? {};
  }

  static async saveConsent(applicationId: string, consentType: UWConsentType, accepted: boolean): Promise<{ saved: boolean }> {
    const res = await httpPost(uwConsentEndpoints.saveConsent(applicationId), {
      consentType,
      accepted,
    });
    return unwrap(res);
  }

  // ── Direct Debit ─────────────────────────────────────

  static async getDirectDebit(applicationId: string): Promise<UWDirectDebit | null> {
    try {
      const res = await httpGet(uwDirectDebitEndpoints.getDirectDebit(applicationId));
      return unwrap<UWDirectDebit | null>(res);
    } catch {
      return null; // 404 = not set yet
    }
  }

  static async saveDirectDebit(applicationId: string, data: UWDirectDebit): Promise<{ saved: boolean }> {
    const res = await httpPost(uwDirectDebitEndpoints.saveDirectDebit(applicationId), data);
    return unwrap(res);
  }

  // ── KYC Verification ────────────────────────────────
  // Note: No 3rd-party IDV integration yet. Frontend sends document metadata
  // to backend for persistence; actual identity check is manual/future.

  static async getKycVerification(applicationId: string): Promise<UWKycVerification | null> {
    try {
      const res = await httpGet(uwKycEndpoints.getVerification(applicationId));
      return unwrap<UWKycVerification | null>(res);
    } catch {
      return null;
    }
  }

  static async saveKycVerification(applicationId: string, data: Partial<UWKycVerification>): Promise<{ verificationId: number }> {
    const res = await httpPost(uwKycEndpoints.saveVerification(applicationId), data);
    return unwrap(res);
  }

  // ── Onfido KYC ──────────────────────────────────────

  static async initializeOnfidoKyc(applicationId: string): Promise<OnfidoInitResponse> {
    const res = await httpPost(uwOnfidoEndpoints.initialize(applicationId));
    return unwrap<OnfidoInitResponse>(res);
  }

  // Dev/testing only — backend guards this to non-production environments.
  static async resetOnfidoKyc(applicationId: string): Promise<void> {
    await httpPost(uwOnfidoEndpoints.reset(applicationId));
  }

  static async getOnfidoKycStatus(applicationId: string): Promise<OnfidoKycStatus | null> {
    try {
      const res = await httpGet(uwOnfidoEndpoints.status(applicationId));
      return unwrap<OnfidoKycStatus | null>(res);
    } catch {
      return null;
    }
  }

  // ── Field Sources ───────────────────────────────────

  static async getFieldSources(applicationId: string): Promise<UWFieldSource[]> {
    const res = await httpGet(uwFieldSourceEndpoints.getFieldSources(applicationId));
    const data = unwrap<any>(res);
    return data?.fieldSources ?? data ?? [];
  }

  // ── Decision ────────────────────────────────────────

  static async getDecision(applicationId: string): Promise<UWDecision> {
    const res = await httpGet(uwDecisionEndpoints.getDecision(applicationId));
    return unwrap<UWDecision>(res);
  }

  // ── Summary / Affordability ─────────────────────────

  static async getSummary(applicationId: string): Promise<UWSummary> {
    const res = await httpGet(uwSummaryEndpoints.getSummary(applicationId));
    return unwrap<UWSummary>(res);
  }

  // ── Open Banking ───────────────────────────────────

  // Backend resolves customerId from the JWT (see uw-ob.controller.ts:loadCustomerByRequest);
  // the client only needs to supply applicationId / searchId context.
  static async initiateOB(applicationId?: string): Promise<OBInitiateResponse> {
    const res = await httpPost(uwOBEndpoints.initiate, applicationId ? { applicationId } : {});
    return unwrap<OBInitiateResponse>(res);
  }

  static async pollOB(searchId: string): Promise<OBPollResponse> {
    const res = await httpPost(uwOBEndpoints.poll, { searchId });
    return unwrap<OBPollResponse>(res);
  }

  // searchId is optional but SHOULD be passed after polling so the backend
  // can unambiguously tell "finalise bank X's just-completed search" apart
  // from "rebuild cached summary on page refresh". Without it, the backend
  // falls back to the app's active lmxSearchId — which is correct for
  // first-bank flows but ambiguous once multiple banks have been imported.
  static async completeOB(applicationId: string, searchId?: string): Promise<OBCompleteResponse> {
    const body = searchId ? { searchId } : {};
    const res = await httpPost(uwOBEndpoints.complete(applicationId), body);
    return unwrap<OBCompleteResponse>(res);
  }

  // Read-only check used by the OB page on mount to decide intro-vs-review
  // when localStorage flags are missing or out of sync (different device,
  // cleared storage, hydration race). Backend returns whether the app has
  // any persisted bank accounts. Never triggers an LMX call or import.
  static async getOBStatus(applicationId: string): Promise<OBStatusResponse> {
    const res = await httpGet(uwOBEndpoints.status(applicationId));
    return unwrap<OBStatusResponse>(res);
  }

  // Persist the "I confirm I have connected all my bank accounts" checkbox.
  // Backend auto-resets this on every initiateOB, so callers don't need to
  // coordinate — just reflect whatever state the user ticked.
  static async setObConfirmed(applicationId: string, confirmed: boolean): Promise<void> {
    const res = await httpPatch(uwOBEndpoints.confirmed(applicationId), { confirmed });
    unwrap(res);
  }

  /**
   * Discard all Open Banking data for an application — deletes persisted
   * transactions, clears the OB snapshot, zeros OB-derived I&E figures, and
   * removes the active LMX search reference. Server refuses with 409 if the
   * customer has ever confirmed OB on this application (the UI hides the
   * Cancel button in that state; this is the matching server-side guard).
   */
  static async discardOB(applicationId: string): Promise<{ discarded: boolean }> {
    const res = await httpPost(uwOBEndpoints.discard(applicationId));
    return unwrap(res);
  }

  /**
   * Clear the active LMX search reference only — leaves imported data intact.
   * Called when the polling timeout fires (LMX is stuck on "Initiated"
   * because the user never completed bank consent) and on Try Again, so the
   * next initiateOB starts cleanly without getOBStatus.pendingSearchId
   * bouncing the user back into polling on a dead search.
   *
   * Safe to call at any time, including after the customer has imported
   * bank-1 and a subsequent bank-2 search has gone dead — only touches
   * lmxSearchId / lmxSearchInitiatedAt, never lmxAccounts.
   */
  static async abandonActiveOBSearch(applicationId: string): Promise<{ abandoned: boolean; hadActiveSearch: boolean }> {
    const res = await httpPost(uwOBEndpoints.abandonSearch(applicationId));
    return unwrap(res);
  }

  /**
   * Download a CSV of persisted LMX transactions. Scope is determined by
   * which params are passed — narrowest wins:
   *   - accountNumber (+ sortCode) → per-account across all searches
   *   - searchId                   → per-search (one LMX fresh pull)
   *   - applicationId              → everything for the application
   * Matches the prod creditor `LMX_ACC_OB_AMOUNTS` column layout.
   */
  static async downloadObTransactionsCsv(params: {
    applicationId?: string;
    searchId?: string;
    accountNumber?: string;
    sortCode?: string;
  }): Promise<void> {
    const url = uwOBEndpoints.transactionsCsv(params);
    const res = await httpGet<Blob>(url, { responseType: 'blob' });
    const blob = res.data instanceof Blob
      ? res.data
      : new Blob([res.data as any], { type: 'text/csv;charset=utf-8' });

    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    let tag: string;
    if (params.accountNumber) tag = `account-${params.accountNumber}${params.sortCode ? `-${params.sortCode}` : ''}`;
    else if (params.searchId) tag = `search-${params.searchId}`;
    else tag = `app-${params.applicationId ?? 'all'}`;
    a.download = `lmx-transactions-${tag}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
  }

  /** List of applications with persisted LMX transactions, joined to
   *  Customer for email/name, broken down by account. Powers the
   *  "LMX Transactions" tab in the debug logs page. */
  static async getObTransactionsSummary(): Promise<{ applications: ObTransactionApplicationSummary[]; total: number }> {
    const res = await httpGet(uwOBEndpoints.transactionsSummary);
    return unwrap(res);
  }

  /**
   * QA aid — download a per-UI-field comparison CSV for one application.
   * One row per UW Phase 2 budget field, showing the OB-derived value, the
   * current UI value, and the field's source (OB / OB-edited / customer).
   * Use alongside `downloadObTransactionsCsv` to verify Open Banking import.
   */
  static async downloadObFieldComparisonCsv(params: { applicationId: string }): Promise<void> {
    const url = uwOBEndpoints.fieldComparisonCsv(params);
    const res = await httpGet<Blob>(url, { responseType: 'blob' });
    const blob = res.data instanceof Blob
      ? res.data
      : new Blob([res.data as any], { type: 'text/csv;charset=utf-8' });

    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = `lmx-field-comparison-app-${params.applicationId}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
  }

  // ── Webhook Logs (debug) ────────────────────────────

  static async getWebhookLogs(params?: { applicationId?: string; purpose?: string; purposes?: string; url?: string; status?: string; limit?: number; offset?: number }): Promise<{ logs: any[]; total: number }> {
    const res = await httpGet(uwWebhookLogEndpoints.getLogs(params));
    return unwrap(res);
  }

  static async retryWebhookLog(logId: string): Promise<{ message: string; logId: string; nextRetryAt: string }> {
    const res = await httpPost(uwWebhookLogEndpoints.retry(logId));
    return unwrap(res);
  }

  // Used by the Equifax tab detail dialog to surface the linked decision
  // (bands + outcome) alongside the raw API logs. Returns null on 404 /
  // not-started — callers should treat as "no decision yet".
  static async getSoftCheckStatusForApp(applicationId: string): Promise<SoftCheckStatusResponse | null> {
    try {
      const res = await httpGet(uwSoftCheckEndpoints.status(applicationId));
      const data = unwrap(res) as SoftCheckStatusResponse;
      if (!data || data.status === 'NOT_STARTED') return null;
      return data;
    } catch {
      return null;
    }
  }

  // Per-application Equifax summary for the UW Logs > Equifax tab. One entry
  // per application that has any soft-check attempt.
  static async getSoftCheckAdminSummary(): Promise<{
    applications: SoftCheckApplicationSummary[];
    total: number;
  }> {
    const res = await httpGet(uwSoftCheckEndpoints.adminSummary);
    return unwrap(res);
  }

  // ── Invite Payload Logs (inbound from CRM / external) ──

  static async getInviteLogs(params?: {
    applicationId?: string;
    source?: string;
    resultStatus?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ logs: any[]; total: number }> {
    const res = await httpGet(uwInviteLogEndpoints.getLogs(params));
    return unwrap(res);
  }

  static async getInviteLog(logId: string): Promise<any> {
    const res = await httpGet(uwInviteLogEndpoints.getById(logId));
    return unwrap(res);
  }

  /**
   * Upload a supporting file to the Documents API (`POST .../api/Documents/upload?storage=hot`).
   * Sends multipart field `file` only. Base URL defaults to `https://uw-apis-node.dynamatix.com`;
   * override with `VITE_UW_DOCUMENTS_API_BASE_URL` if needed.
   */
  static async uploadDocumentHotStorage(
    file: File,
    params: {
      customerId?: string | number | null;
      isDocUploadedFromDecl?: string;
      entrySource?: string;
    } = {},
  ): Promise<UploadDocumentResponse> {
    const formData = new FormData();
    formData.append('file', file);
    const decl = params.isDocUploadedFromDecl ?? '';

    const baseURL = uwDocumentsApiBaseURL();

    try {
      const res = await httpPost<unknown>(UW_DOCUMENTS_UPLOAD_HOT_STORAGE_PATH, formData, {
        baseURL,
      });
      const body = extractUwDocumentUploadBody(res);
      return mapUwDocumentUploadToUploadDocumentResponse(body, {
        file,
        customerId: params.customerId,
        isDocUploadedFromDecl: decl,
      });
    } catch (error) {
      femaCapture('uw_document_upload_error', error, {
        category: 'api',
        severity: 'error',
        skipSanitization: false,
        user: {},
      });
      throw error;
    }
  }
}

// ── Open Banking Types ───────────────────────────────

export interface OBInitiateResponse {
  success: boolean;
  bankLink?: string;
  providerLogoUri?: string;
  providerDisplayName?: string;
  status?: string;
  successful?: boolean;
  inProgress?: boolean;
}

export interface OBPollResponse {
  success: boolean;
  status: string;
  successful: boolean;
  inProgress?: boolean;
  providerDisplayName?: string;
  providerLogoUri?: string;
  bankLink?: string;
}

export interface OBConnectedAccount {
  accountId?: string;
  institutionName?: string;
  accountNumber?: string;
  sortCode?: string;
  balance?: number;
  searchId?: string;
  importedAt?: string;
  /** LMX `InstitutionID` resolved at import time. Used for refresh / debugging. */
  institutionId?: number;
  /** Base64-encoded SVG (`data:image/svg+xml;base64,...`) of the bank's logo,
   *  fetched server-side via LMX's Institution Image API. Optional — the UI
   *  falls back to a placeholder icon when this is missing. */
  logo?: string;
}

export interface ObTransactionAccountSummary {
  accountNumber: string | null;
  sortCode: string | null;
  institutionName: string | null;
  lmxAccountId: string | null;
  nickName: string | null;
  transactionCount: number;
  latestBookingDate: string | null;
  latestImportAt: string | null;
  searchIds: string[];
}

export interface ObTransactionApplicationSummary {
  applicationId: string;
  customerId: string | null;
  customerEmail: string | null;
  customerName: string | null;
  accountCount: number;
  transactionCount: number;
  latestImportAt: string | null;
  accounts: ObTransactionAccountSummary[];
}

export type EquifaxStage = 'EQUIFAX_SEARCH' | 'DECISION_MATRIX' | 'UW_PORTAL';

export interface SoftCheckApplicationSummary {
  applicationId: string;
  customerId: string | null;
  customerEmail: string | null;
  customerName: string | null;
  latest: {
    softCheckId: string;
    status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETE' | 'ERROR';
    outcome: string | null;
    softCheckBand: 'A' | 'B' | 'C' | 'D' | 'E' | null;
    errorCode: string | null;
    errorMessage: string | null;
    startedAt: string;
    completedAt: string | null;
  } | null;
  attemptCount: number;
  callCounts: Record<EquifaxStage, number>;
  totalCalls: number;
  latestCallAt: string | null;
}

export interface OBStatusResponse {
  hasConnectedBanks: boolean;
  accountCount: number;
  // Set when the application has an active LMX search whose data has not
  // been imported yet AND no banks are connected — i.e. the customer kicked
  // off OB, returned to the page, and we have nothing on file. The OB page
  // uses this to auto-resume polling without needing the SearchID in the
  // URL (LMX's configured redirect URL doesn't propagate it through our
  // hash-router today). Null when there is nothing to resume.
  pendingSearchId?: string | null;
}

export interface OBCompleteResponse {
  imported: boolean;
  alreadyImported?: boolean;
  accountCount?: number;
  accounts?: OBConnectedAccount[];
  // Persisted state of the OB complete screen's "I've connected all my
  // banks" checkbox. Server resets this to false on every initiateOB.
  obAllConfirmed?: boolean;
  // Write-once flag — true once the customer has confirmed OB at least once
  // on this application. Stays true even after Add Another Bank or in-session
  // untick. Drives Cancel button visibility (and is enforced server-side as
  // a discard guard, so this is purely a UI hint).
  obEverConfirmed?: boolean;
  transactionSummary?: {
    totalIncome: number;
    totalExpenses: number;
    incomeTransCount: number;
    expenseTransCount: number;
    /** ISO timestamps of the LMX pull window we asked for (NOT min/max of
     *  actual booking dates — those would lie if the customer happened to
     *  have no transactions in the first month of the window). Computed
     *  backend-side as `[lmxSearchInitiatedAt − duration, lmxSearchInitiatedAt]`.
     *  Either may be null for older imports without the field. */
    pullWindowStart?: string | null;
    pullWindowEnd?: string | null;
  };
  reason?: string;
}
